<template>
  <div class="body bank-app">
    <Navbar 
    :showBackButton="true" 
    @back="goBack"
    />
        
    <div class="main-content">
      <!-- Card Services Section - Horizontal Scroll -->
      <div class="horizontal-services-section">
        <h3 class="section-title">{{ $t('systemAdmin.dashboard.popular_services') }}</h3>
        <div class="horizontal-scroll-container">
          <div class="scroll-content">
            <div class="scroll-service-item" @click="navigateOffice">
              <div class="scroll-service-icon">
                <img src="@/assets/Icons/reportMande.png" />
              </div>
              <span class="scroll-service-label">{{ $t('systemAdmin.dashboard.office_letters') }}</span>
            </div>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import Navbar from '@/components/Navbar.vue'
import { useRouter } from 'vue-router'
import { adminCompanyService } from '@/services/api'

export default {
  name: 'BankDashboard',
  components: {
    Navbar
  },
  setup() {
    const authStore = useAuthStore()
    const remainingCash = ref(0)
    const loading = ref(false)
    const error = ref(null)
    const router = useRouter()

    const currencyPrices = ref([])
    const currencyLoading = ref(false)
    const currencyError = ref(null)
    const refreshingCurrencies = ref(false)
    const showAllCurrencies = ref(false)
    const currencyUpdating = ref({})
    const showAgreementModal = ref(false)
    const agreementAccepted = ref(false)
    const checkingAgreement = ref(false)

    const adminOfAdmin = ref(false)

    
    // Popular currencies to show by default
    const popularCurrencies = ['USD', 'EUR', 'AED', 'CNY']
    
    // Get displayed currencies based on state
    const displayedCurrencies = computed(() => {
      if (!currencyPrices.value || currencyPrices.value.length === 0) {
        return []
      }
      
      if (showAllCurrencies.value) {
        return currencyPrices.value
      } else {
        return currencyPrices.value.filter(currency => 
          currency && currency['کد ارز'] && popularCurrencies.includes(currency['کد ارز'])
        )
      }
    })

    const hasPermission = (permissionKey) => {
      // Safe guard: check if permissions is a valid array first
      return Array.isArray(authStore.permissions) && authStore.permissions.includes(permissionKey)
    }
    
    // Check if currency is popular
    const isPopularCurrency = (code) => {
      return code && popularCurrencies.includes(code)
    }
    
    // Get flag emoji for currency
    const getCurrencyFlag = (code) => {
      const flagMap = {
        'USD': '🇺🇸',
        'EUR': '🇪🇺',
        'GBP': '🇬🇧',
        'CAD': '🇨🇦',
        'TRY': '🇹🇷',
        'AED': '🇦🇪',
        'JPY': '🇯🇵',
        'AUD': '🇦🇺',
        'NZD': '🇳🇿',
        'CHF': '🇨🇭',
        'PKR': '🇵🇰',
        'AZN': '🇦🇿',
        'NOK': '🇳🇴',
        'SEK': '🇸🇪',
        'DKK': '🇩🇰',
        'KWD': '🇰🇼',
        'OMR': '🇴🇲',
        'RUB': '🇷🇺',
        'BRL': '🇧🇷',
        'THB': '🇹🇭',
        'AFN': '🇦🇫',
        'INR': '🇮🇳',
        'CNY': '🇨🇳',
        'MYR': '🇲🇾'
      }
      return flagMap[code] || '💰'
    }
    
    // Format currency price with Persian numbers
    const formatCurrencyPrice = (price) => {
      if (!price || price === "0" || price === 0) return '۰'
      
      try {
        // Handle if price is already a string with commas
        const cleanPrice = price.toString().replace(/[,\u066B\u066C]/g, '');
        
        // Convert to number to handle decimal points if any
        const numPrice = parseFloat(cleanPrice);
        
        if (isNaN(numPrice)) return '۰'
        
        // Format with thousand separators
        const formatted = numPrice.toLocaleString('fa-IR');
        
        return formatted;
        
      } catch (error) {
        console.error('Error formatting price:', price, error)
        
        // Fallback manual formatting
        try {
          const priceStr = price.toString().replace(/[^0-9]/g, '');
          const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
          
          // Convert to Persian digits
          let persianStr = '';
          for (let i = 0; i < priceStr.length; i++) {
            const digit = parseInt(priceStr[i]);
            if (!isNaN(digit)) {
              persianStr += persianDigits[digit];
            }
          }
          
          // Add thousand separators from right to left
          let result = '';
          let count = 0;
          
          for (let i = persianStr.length - 1; i >= 0; i--) {
            result = persianStr[i] + result;
            count++;
            if (count === 3 && i > 0) {
              result = '،' + result;
              count = 0;
            }
          }
          
          return result;
        } catch (fallbackError) {
          return price || '۰'
        }
      }
    }
    
    // Format currency time
    const formatCurrencyTime = () => {
      try {
        // Get current time in Tehran (Asia/Tehran timezone)
        const now = new Date();
        
        // Format time in Persian locale with Tehran timezone
        const formatter = new Intl.DateTimeFormat('fa-IR', {
          timeZone: 'Asia/Tehran',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false // 24-hour format
        });
        
        return formatter.format(now);
        
      } catch (error) {
        console.error('Error formatting Tehran time:', error);
        
        // Fallback: simple calculation
        const now = new Date();
        const tehranOffset = 3.5 * 60 * 60 * 1000; // Tehran is UTC+3:30
        const tehranTime = new Date(now.getTime() + tehranOffset);
        
        const hours = tehranTime.getUTCHours().toString().padStart(2, '0');
        const minutes = tehranTime.getUTCMinutes().toString().padStart(2, '0');
        
        // Convert to Persian digits
        const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
        const persianHours = hours.split('').map(digit => persianDigits[digit] || digit).join('');
        const persianMinutes = minutes.split('').map(digit => persianDigits[digit] || digit).join('');
        
        return `${persianHours}:${persianMinutes}`;
      }
    };
    
    // Fetch currency prices
    const fetchCurrenciesPrice = async () => {
      if (!authStore.user?.customerId) {
        console.error('No customer ID found')
        currencyError.value = 'کاربر احراز هویت نشده است'
        return
      }

      currencyLoading.value = true
      currencyError.value = null

      try {
        console.log('Fetching currency prices for customer ID:', authStore.user.subCodeId)
        
        //const response = await employerService.getCurrenciesPrice()
        const response = null
        console.log('Currency prices response:', response)
        
        if (response.data && response.data.success) {
          // Ensure data is an array
          const prices = response.data.currency_prices || []
          currencyPrices.value = Array.isArray(prices) ? prices : []
          
          if (currencyPrices.value.length === 0) {
            currencyError.value = 'داده‌ای یافت نشد'
          } else {
            // Sort by popular currencies first
            currencyPrices.value.sort((a, b) => {
              const aIsPopular = popularCurrencies.includes(a['کد ارز'])
              const bIsPopular = popularCurrencies.includes(b['کد ارز'])
              if (aIsPopular && !bIsPopular) return -1
              if (!aIsPopular && bIsPopular) return 1
              return 0
            })
          }
        } else {
          currencyError.value = response.data?.message || 'خطا در دریافت داده‌ها'
          currencyPrices.value = []
        }

      } catch (err) {
        console.error('Error fetching currency prices:', err)
        currencyError.value = err.response?.data?.detail || 'خطا در ارتباط با سرور'
        currencyPrices.value = []
      } finally {
        currencyLoading.value = false
        refreshingCurrencies.value = false
      }
    }
    
    // Refresh currency prices with animation
    const refreshCurrencyPrices = async () => {
      if (refreshingCurrencies.value || currencyLoading.value) return
      
      refreshingCurrencies.value = true
      currencyError.value = null
      
      // Show updating animation for popular currencies
      popularCurrencies.forEach(code => {
        currencyUpdating.value[code] = true
      })
      
      // Wait a bit for animation
      setTimeout(async () => {
        await fetchCurrenciesPrice()
        
        // Reset updating state
        Object.keys(currencyUpdating.value).forEach(key => {
          currencyUpdating.value[key] = false
        })
      }, 300)
    }

    const navigateTraz = () => {
      router.push('/employer/traz')
    }

    const navigateMande = () => {
      router.push('/employer/report-mande')
    }

    const navigateMande2 = () => {
      router.push('/employer/report-mande2')
    }

    const navigateTaseer = () => {
      router.push('/employer/taseer-sood')
    }

    const navigateOperator = () => {
      router.push('/admin/operator-management')
    }

    const navigateCompanyPermission = () => {
      router.push('/admin/company-permissions')
    }

    const navigateUserManagement = () => {
      router.push('/admin/user-management')
    }

    const navigateCompanyManagement = () => {
      router.push('/admin/company-management')
    }

    const navigateOffice = () => {
      router.push('/employee/employee-letters')
    }

    const navigateCustomerSearch = () => {
      router.push('/employer/customer-search')
    }

    const goBack = () => {
      router.push('/select-role')
    }

    const formatRemainingCash = (amount) => {
      if (!amount && amount !== 0) return '۰'
      const absoluteAmount = Math.abs(amount)
      return new Intl.NumberFormat('fa-IR').format(absoluteAmount).replace('ریال', '') + ' ریال'
    }

    const formatBestanKari = (amount) => {
      if (!amount && amount !== 0) return '۰'
      if (amount > 0){
        return 'بدهکار'
      } else {
        return 'بستانکار'
      }
    }

    const confirmAgreement = async () => {
      try {
        await adminCompanyService.acceptAgreement()
        showAgreementModal.value = false
        agreementAccepted.value = false

        await fetchCurrenciesPrice()
      } catch (err) {
        console.error('Failed to accept agreement', err)
        alert('خطا در ثبت توافق‌نامه')
      }
    }

    return {
      remainingCash,
      loading,
      error,
      currencyPrices,
      currencyLoading,
      currencyError,
      refreshingCurrencies,
      showAllCurrencies,
      displayedCurrencies,
      popularCurrencies,
      adminOfAdmin,
      isPopularCurrency,
      getCurrencyFlag,
      formatCurrencyPrice,
      formatCurrencyTime,
      fetchCurrenciesPrice,
      refreshCurrencyPrices,
      formatRemainingCash,
      formatBestanKari,
      navigateTraz,
      navigateMande,
      navigateMande2,
      navigateTaseer,
      navigateOffice,
      navigateCustomerSearch,
      navigateOperator,
      navigateCompanyPermission,
      navigateCompanyManagement,
      navigateUserManagement,
      goBack,
      confirmAgreement,
      agreementAccepted,
      showAgreementModal,
      checkingAgreement,
      hasPermission
    }
  }
}
</script>

<style scoped>
.back-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  color: var(--primary);
}

.back-arrow:hover {
  background-color: var(--hover);
}

.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: var(--bg);
  direction: rtl;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 80px;
}

.account-summary {
  background: linear-gradient(135deg, var(--gradient-1), var(--gradient-2));
  color: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px var(--shadow-1);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  opacity: 0.9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: 0.8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
  color: white;
}

.section-title {
  font-size: 16px;
  margin: 20px 0 12px;
  font-weight: 600;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.service-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.service-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: var(--primary);
  font-weight: bold;
  box-shadow: 0 2px 4px var(--shadow-1);
  border: 1px solid var(--card-border);
}

.service-label {
  font-size: 12px;
  color: var(--subtext);
}

.additional-text {
  background-color: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 2px 8px var(--shadow-1);
  color: var(--subtext);
  border: 1px solid var(--card-border);
}

.account-number {
  margin-top: 8px;
  font-weight: bold;
  color: var(--primary);
}

/* Horizontal Scroll Services */
.horizontal-services-section {
  margin-bottom: 24px;
}

.horizontal-scroll-container {
  overflow-x: auto;
  padding: 8px 0;
  margin: 0 -16px;
  padding: 8px 16px;
}

.horizontal-scroll-container::-webkit-scrollbar {
  display: none;
}

.scroll-content {
  display: flex;
  gap: 16px;
  padding: 4px 0;
  min-width: min-content;
}

.scroll-service-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 80px;
  flex-shrink: 0;
  margin-right: auto;
  margin-left: auto;
}

.scroll-service-item:hover {
  transform: scale(1.2);
  cursor: pointer;
  background-color: var(--hover);
  transform: translateY(-2px);
}

.scroll-service-item:active {
  transform: translateY(0);
}

.scroll-service-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background-color: var(--card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: var(--primary);
  font-weight: bold;
  box-shadow: 0 2px 8px var(--shadow-1);
  border: 1px solid var(--card-border);
  overflow: hidden;
}

.scroll-service-icon img {
  max-width: 70%;
  max-height: 70%;
  object-fit: contain;
}

.scroll-service-label {
  font-size: 11px;
  color: var(--subtext);
  line-height: 1.3;
  max-width: 80px;
}

/* Original Services List */
.services-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.service-card {
  display: flex;
  align-items: center;
  background-color: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px var(--shadow-1);
  border: 1px solid var(--card-border);
}

.service-card .card-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  font-size: 12px;
  color: var(--primary);
  font-weight: bold;
}

.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-around;
  background-color: var(--card-bg);
  padding: 12px 0;
  box-shadow: 0 -2px 10px var(--shadow-1);
  z-index: 100;
  border-top: 1px solid var(--card-border);
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 12px;
  color: var(--subtext);
}

.nav-icon {
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
  border-radius: 50%;
}

.home-icon {
  background-color: var(--primary);
}

.services-icon, .cards-icon, .profile-icon {
  background-color: var(--bg);
  border: 1px solid var(--card-border);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .services-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }
  
  .service-icon {
    width: 50px;
    height: 50px;
    font-size: 10px;
  }
  
  .service-label {
    font-size: 10px;
  }

  .scroll-service-icon {
    width: 55px;
    height: 55px;
    font-size: 11px;
  }

  .scroll-service-label {
    font-size: 10px;
    max-width: 70px;
  }
}

.accounting-section {
  margin-bottom: 24px;
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.accounting-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px var(--shadow-1);
  border: 1px solid var(--card-border);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px var(--shadow-2);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--card-border);
}

.record-date {
  font-size: 12px;
  color: var(--subtext);
  font-weight: 500;
}

.record-amount {
  font-size: 14px;
  font-weight: bold;
}

.record-amount.debit {
  color: var(--orange-1);
}

.record-amount.credit {
  color: var(--green);
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.detail-label {
  font-size: 11px;
  color: var(--muted);
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 12px;
  color: var(--text);
  text-align: left;
  flex: 1;
}

.type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.debit-badge {
  background-color: rgba(231, 76, 60, 0.1);
  color: var(--orange-1);
}

.credit-badge {
  background-color: rgba(39, 174, 96, 0.1);
  color: var(--green);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--subtext);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--card-border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--muted);
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px dashed var(--card-border);
}

/* Accounting Card Button */
.accounting-card .card-body button {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px var(--shadow-1);
  margin-top: 8px;
}

.accounting-card .card-body button:hover {
  background: linear-gradient(135deg, var(--gradient-2) 0%, var(--gradient-1) 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px var(--shadow-2);
}

.accounting-card .card-body button:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px var(--shadow-1);
}

.accounting-card .card-body button:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--ring);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--modal-overlay);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.request-modal {
  width: 90%;
  max-width: 480px;
  background: var(--card-bg);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 5px 20px var(--shadow-2);
  direction: rtl;
  border: 1px solid var(--card-border);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--card-border);
  padding-bottom: 10px;
}

.modal-header h3 {
  color: var(--heading);
}

.close-btn {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: var(--subtext);
}

.close-btn:hover {
  color: var(--orange-1);
}

.form-row {
  margin: 12px 0;
  display: flex;
  flex-direction: column;
}

.form-row label {
  margin-bottom: 4px;
  font-weight: bold;
  color: var(--heading);
}

.form-row input,
.form-row select {
  padding: 10px;
  border: 1px solid var(--card-border);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg);
  color: var(--text);
}

.form-row input:focus,
.form-row select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 2px var(--ring);
}

.modal-footer {
  text-align: center;
  margin-top: 15px;
}

.submit-btn {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  color: white;
  padding: 10px 22px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 15px;
}

.submit-btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

/* Currency Prices Section */
.currency-prices-section {
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  border-radius: 15px;
  padding: 15px;
  margin: 15px 0;
  color: white;
  box-shadow: 0 4px 15px var(--shadow-1);
  transition: all 0.3s ease;
  max-height: 320px;
  overflow: hidden;
}

.currency-prices-section.expanded {
  max-height: 600px;
  overflow-y: auto;
}

.currency-prices-section.expanded::-webkit-scrollbar {
  width: 6px;
}

.currency-prices-section.expanded::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  margin: 5px;
}

.currency-prices-section.expanded::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}

.currency-prices-section.expanded::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.4);
}

.currency-prices-section.expanded {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);
}

.currency-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
  padding: 5px 0;
}

.currency-header .section-title {
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0;
  color: white;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.refresh-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  font-size: 1rem;
}

.expand-btn {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
}

.expand-btn:hover {
  background: rgba(255, 255, 255, 0.25);
}

.expand-btn.expanded {
  transform: rotate(180deg);
}

.refresh-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-btn.rotating .refresh-icon {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.currency-content {
  height: calc(100% - 50px);
}

.currency-grid-container {
  height: 100%;
  overflow-y: hidden;
}

.currency-prices-section.expanded .currency-grid-container {
  overflow-y: visible;
}

.currency-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
  margin-bottom: 15px;
}

.currency-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 5px;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 5px;
}

.currency-card:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
  box-shadow: 0 3px 10px var(--shadow-1);
}

.currency-card.highlighted {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.currency-flag {
  font-size: 1.5rem;
  margin-left: 10px;
  min-width: 30px;
  text-align: center;
}

.currency-info {
  flex: 1;
  min-width: 0;
}

.currency-code {
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.currency-name {
  font-size: 0.75rem;
  opacity: 0.9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.currency-price {
  text-align: left;
  min-width: 60px;
}

.price-value {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  direction: ltr;
  text-align: right;
}

.price-time {
  font-size: 0.65rem;
  opacity: 0.8;
  direction: ltr;
  text-align: right;
}

/* Currency States */
.currency-loading,
.currency-error,
.currency-empty {
  padding: 20px 0;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.currency-error {
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.3);
  border-radius: 8px;
  padding: 15px;
}

.error-icon {
  font-size: 1.5rem;
}

.retry-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 15px;
  border-radius: 15px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.currency-empty {
  opacity: 0.8;
}

.empty-icon {
  font-size: 2.5rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .accounting-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .accounting-card {
    padding: 12px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .record-amount {
    align-self: flex-end;
  }
  
  .currency-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .currency-header .section-title {
    font-size: 1.1rem;
  }
  
  .currency-prices-section {
    max-height: 280px;
    margin: 10px 0;
    padding: 12px;
  }
  
  .currency-prices-section.expanded {
    max-height: 500px;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .accounting-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .accounting-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 480px) {
  .currency-grid {
    grid-template-columns: 1fr;
  }
  
  .currency-prices-section {
    border-radius: 12px;
    max-height: 260px;
  }
  
  .currency-prices-section.expanded {
    max-height: 450px;
  }
}

/* Desktop view */
@media (min-width: 769px) {
  .currency-prices-section {
    max-width: 100%;
    margin: 15px auto;
    padding: 18px;
    max-height: 300px;
  }
  
  .currency-prices-section.expanded {
    max-height: 550px;
  }
  
  .currency-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 15px;
  }
  
  .currency-header .section-title {
    font-size: 1.3rem;
  }
  
  .currency-code {
    font-size: 1.1rem;
  }
  
  .currency-name {
    font-size: 0.8rem;
  }
  
  .price-value {
    font-size: 1.1rem;
  }
}

/* Large desktop */
@media (min-width: 1200px) {
  .currency-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease;
}

.modal-content {
  background-color: var(--bg);
  border-radius: 20px;
  padding: 30px;
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text);
}

.modal-close {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #999;
  transition: color 0.3s;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.modal-close:hover {
  color: #e53e3e;
  background: #fff5f5;
}

.agreement-modal{
  max-height:90vh;
  overflow:hidden;
  display:flex;
  flex-direction:column;
  color: var(--text);
}

.agreement-content{
  overflow-y:auto;
  max-height:50vh;
  padding:10px;
  line-height:1.8;
  color: var(--subtext);
}

.agreement-checkbox{
  margin-top:15px;
  font-weight:500;
}
</style>